import { firstBy } from "thenby";
import { CalendarDateTime, parseDateTime } from "@internationalized/date";

import { isBetween } from "commons/src/internationalizedUtils";
import { Document, Account } from "commons/src/types/types";

export const DEFAULT_DOCUMENTS_FILTER = { value: "all", display: "All documents" };
export const DEFAULT_ACCOUNT_FILTER = { value: "default", display: "All accounts" };

export const getAccountWrapperTitle = (
    document: Document,
    accountsMap: Record<string, Pick<Account, "wrapperType">>
) => {
    const accountNumber = document?.accountNumber;

    let accountWrapperTitle =
        accountNumber && accountNumber !== DEFAULT_ACCOUNT_FILTER.display ? accountNumber : "";
    if (accountsMap && accountsMap[accountNumber]) {
        accountWrapperTitle += ` ${accountsMap[accountNumber].wrapperType}`;
    }
    return accountWrapperTitle;
};

export const getDocumentsSinceLastLogin = (
    json:
        | {
              page?: { number?: number } | undefined;
              _embedded: { correspondenceResourceList: Document[] } | undefined;
          }
        | undefined,
    lastLogin: string
) => {
    const docsSinceLastLogin: Document[] = [];
    if (json?.page?.number === 0) {
        json?._embedded?.correspondenceResourceList?.forEach(doc => {
            if (doc?.sentDate > lastLogin) {
                docsSinceLastLogin.push(doc);
            }
        });
    }
    return docsSinceLastLogin;
};

export function getDocumentIdString(id: string | number) {
    return `${id}_DOCUMENT`;
}

export function getDocumentsWithUpdatedCategories(documents: Document[] | undefined) {
    if (!documents) {
        return [];
    }

    return documents.map(currentDocument => {
        const category =
            currentDocument.category === "None" ? "Miscellaneous" : currentDocument.category;
        return { ...currentDocument, category };
    });
}

export const getDocumentCategoryOptions = (
    documents: Document[]
): {
    value: string;
    display: string;
}[] => {
    const categories = documents
        .map(currentDocument => currentDocument.category)
        .filter((val, id, arr) => arr.indexOf(val) === id)
        .map(val => ({
            value: val,
            display:
                documents.find(currentDocument => currentDocument.category === val)?.category || ""
        }))
        .sort(firstBy("display"));
    return [DEFAULT_DOCUMENTS_FILTER, ...categories];
};

export const documentAccountSelectionFormatter = (
    account: Pick<Account, "accountNumber" | "wrapperType">
) => {
    return `${account.wrapperType} ${account.accountNumber}`;
};

export const filterAccounts = (doc: Document, account: string) => {
    return (
        account === "Combined" ||
        account === "default" ||
        doc.accountNumber === DEFAULT_ACCOUNT_FILTER.display ||
        doc.accountNumber === account
    );
};

export const filterCategories = (doc: Document, documentCategory: string) =>
    documentCategory === DEFAULT_DOCUMENTS_FILTER.value || doc.category === documentCategory;

export const filterDates = (
    doc: Document,
    actualRange: { startDate: CalendarDateTime; endDate: CalendarDateTime }
) => {
    const docDate = parseDateTime(doc.sentDate);
    return isBetween(docDate, {
        startDate: actualRange.startDate,
        endDate: actualRange.endDate.set({ hour: 23, minute: 59, second: 59 })
    });
};
